import { BadgeCheck, FileCheck, Handshake, KeyRound, ShieldCheck, TrendingUp, Watch } from "lucide-react";
import GuideTemplate from "@/pages/GuideTemplate";

const CFG = {
  seo: {
    testid: "watch-guide",
    path: "/pre-owned-watch-guide",
    image: "/assets/watch-guide-hero.jpg",
    title: "Buying a Pre-Owned Swiss Watch — Authentication, Value & What to Check | Gold Time Jewelry",
    description:
      "A Texas family jeweler's honest guide to buying pre-owned Swiss watches: why pre-owned wins, what box & papers prove, how fakes get caught, and how every watch we sell is checked before it's offered. Gold Time Jewelry — Katy, Copperas Cove & Killeen TX.",
  },
  hero: {
    eyebrow: "The Pre-Owned Watch Guide",
    title: "Buying a pre-owned Swiss watch,",
    titleAccent: "the smart way",
    intro:
      "The best-value Swiss watches in the world aren't in boutique windows — they're pre-owned: first-owner depreciation already paid, discontinued references you can't buy new, and no waitlist. The only thing that matters is buying from someone who opens the caseback before they name a price. Here's how we do it.",
    image: "/assets/watch-guide-hero.jpg",
    imageAlt: "Pre-owned Rolex Submariner with original green box and warranty papers on dark slate",
    ctas: [
      { to: "/collections/watches", label: "Shop watches" },
      { to: "https://webuyswisswatches.com", label: "Sell your Swiss watch", external: true },
    ],
  },
  facts: {
    title: "Why pre-owned",
    titleAccent: "wins",
    paragraphs: [
      "A new Swiss watch takes its biggest value hit the day it leaves the boutique. Buy pre-owned and the first owner has already paid that bill for you — you get the same engineering, the same heritage, often with box and papers, for meaningfully less.",
      "Pre-owned is also the only door into discontinued and waitlisted references. When a model leaves production, the pre-owned market becomes the whole market — and condition plus provenance set the price.",
    ],
    items: [
      { icon: TrendingUp, label: "First-owner depreciation paid" },
      { icon: KeyRound, label: "Discontinued & waitlist models" },
      { icon: ShieldCheck, label: "Authenticated before sale" },
    ],
  },
  showcase: {
    title: "Authenticated first,",
    titleAccent: "before it's ever offered",
    paragraphs: [
      "Every pre-owned watch that comes through our doors is opened and inspected: movement caliber checked against the reference, serial and model numbers checked, case and bracelet examined for swapped or refinished parts, and timing tested on the machine.",
      "If a watch doesn't pass, it doesn't get priced — it gets sent back. What reaches our showcase is what we'd wear ourselves.",
    ],
    image: "/assets/watch-guide-auth.jpg",
    imageAlt: "Watchmaker in white gloves inspecting an opened Swiss automatic movement",
    imageFirst: true,
  },
  table: {
    title: "What to check on",
    titleAccent: "any pre-owned watch",
    columns: ["What it is", "Why it matters"],
    rows: [
      { label: "Box & papers", values: ["Original presentation box, warranty card and booklets", "Proves provenance and date of sale; full sets resell for measurably more"] },
      { label: "Serial & reference", values: ["Engraved numbers on case and papers", "Confirms the watch is what it's claimed to be — and not on a stolen list"] },
      { label: "Movement", values: ["The caliber inside the case", "Correct, clean, original movement keeping time — the heart of authenticity"] },
      { label: "Case & bracelet", values: ["Metal condition, edges, clasp, links", "Heavy polishing erodes value; stretched bracelets hint at hard wear"] },
      { label: "Service history", values: ["Records of maintenance", "A serviced movement runs years longer — ask who serviced it and when"] },
    ],
  },
  cards: {
    title: "The three real",
    titleAccent: "advantages",
    items: [
      {
        icon: TrendingUp,
        title: "Value",
        text: "Someone else paid the steepest depreciation. You buy closer to the watch's true, stable value — and many sought-after references appreciate from there.",
      },
      {
        icon: KeyRound,
        title: "Access",
        text: "Discontinued dials, vintage references, waitlist models — the pre-owned market holds what boutiques can't sell you at any price.",
      },
      {
        icon: Handshake,
        title: "Confidence",
        text: "Buy from a jeweler who inspects every watch and stands behind the piece afterward. A stranger's listing can't offer you that; we can.",
      },
    ],
  },
  honestTake: {
    title: "Our honest take",
    text: "Buy the seller before the watch. A genuine pre-owned Swiss piece with light wear from a dealer who opens casebacks is a better buy than a 'mint, never worn' bargain from a stranger — every time. Condition over year, originality over shine, and papers whenever you can get them. And if a deal feels too good, it isn't a deal. Bring it to us before you pay — we'll look at it with you.",
    image: "/assets/watch-guide-lifestyle.jpg",
    imageAlt: "Wrist wearing a steel Swiss luxury sports watch with a shirt cuff",
  },
  faqs: [
    {
      q: "Does a pre-owned watch come with any warranty?",
      a: "Every pre-owned watch we sell is inspected for authenticity and function before it's offered, and described honestly — condition included. Because each pre-owned piece is one of a kind, ask our team about the specific watch you're considering and we'll tell you exactly what stands behind it. Private-party purchases carry nothing at all, which is a large part of what a dealer's price buys.",
    },
    {
      q: "Is a polished watch worth less?",
      a: "Usually, yes. Collectors pay for original, unpolished cases with sharp factory edges — each polish removes metal and softens lines permanently. A light professional refinish is acceptable on a daily wearer, but heavy polishing is a real value deduction. We disclose the condition of every case we sell.",
    },
    {
      q: "How much do box and papers matter?",
      a: "For popular Swiss references, a full set typically adds 10–20% to resale value and makes the watch easier to sell later. A watch without papers isn't automatically suspect — but it must authenticate on its own merits, which is exactly what our pre-sale inspection is for.",
    },
    {
      q: "Will a pre-owned Swiss watch hold its value?",
      a: "Sought-after steel sports models from the major Swiss houses have historically held or grown value far better than most purchases — but buy a watch because you'll love wearing it, with value retention as a bonus rather than a promise.",
    },
    {
      q: "Can you service my watch later?",
      a: "Yes — bring it to any showroom. From pressure tests and regulation to full movement overhauls, we assess the watch with you and quote the work before anything is done.",
    },
    {
      q: "I have a Swiss watch to sell — how does that work?",
      a: "Start online at our sister service, WeBuySwissWatches.com, for a valuation — or bring the watch to any Gold Time showroom for an in-person offer. Either way: authenticated, weighed and priced in front of you, with a straight answer on what it's worth and why.",
    },
  ],
  cta: {
    heading: "Have a Swiss watch to sell?",
    text: "Get a valuation online from our sister team at WeBuySwissWatches — or bring it to any of our three Texas showrooms and watch us authenticate it at the counter.",
    bgImage: "/assets/watch-guide-cta.jpg",
    buttons: [
      { to: "https://webuyswisswatches.com", label: "Get a watch valuation", external: true },
      { to: "/showrooms", label: "Visit a showroom" },
    ],
  },
  moreGuides: [
    { to: "/diamond-buying-guide", label: "How to Buy a Diamond Without Overpaying" },
    { to: "/gold-karat-guide", label: "10K vs 14K vs 18K Gold — What the Stamp Means" },
  ],
};

export default function WatchGuidePage() {
  return <GuideTemplate cfg={CFG} />;
}
